/** @jsx jsx */
import { Global, css, jsx } from '@emotion/react';

/** @jsxFrag React.Fragment */
import * as React from 'react';

import { getColorSchemePreference } from '@coursera-web/appearance';

const ColorThemeContext = React.createContext<{
  colorTheme: string;
  onColorThemeChange: (colorTheme: string) => void;
}>({
  colorTheme: 'light',
  onColorThemeChange: () => {},
});

export const useColorTheme = () => {
  return React.useContext(ColorThemeContext);
};

const SyncHtmlAtribute = () => {
  const { colorTheme } = useColorTheme();
  React.useEffect(() => {
    document.documentElement.setAttribute('data-color-theme', colorTheme);
  }, [colorTheme]);
  return null;
};

export const ColorThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [colorTheme, setColorTheme] = React.useState<string>(getColorSchemePreference() || 'light');
  return (
    <ColorThemeContext.Provider value={{ colorTheme, onColorThemeChange: setColorTheme }}>
      <SyncHtmlAtribute />
      {/* Hoping to get rid of these variables here once CDS officially supports dark mode variables https://coursera.atlassian.net/browse/CDS-1656 */}
      <Global
        styles={css`
          /* stylelint-disable function-disallowed-list */
          :root[data-color-theme='dark'] {
            --cds-color-darken-975: rgb(15 17 20 / 0%);
            --cds-color-darken-950: rgb(15 17 20 / 3%);
            --cds-color-darken-900: rgb(15 17 20 / 5%);
            --cds-color-darken-800: rgb(15 17 20 / 10%);
            --cds-color-darken-700: rgb(15 17 20 / 20%);
            --cds-color-darken-600: rgb(15 17 20 / 30%);
            --cds-color-darken-500: rgb(15 17 20 / 40%);
            --cds-color-darken-400: rgb(15 17 20 / 50%);
            --cds-color-darken-300: rgb(15 17 20 / 60%);
            --cds-color-darken-200: rgb(15 17 20 / 70%);
            --cds-color-darken-100: rgb(15 17 20 / 80%);
            --cds-color-darken-50: rgb(15 17 20 / 90%);
            --cds-color-darken-25: rgb(15 17 20 / 95%);
            --cds-color-darken-0: rgb(15 17 20 / 98%);
            --cds-color-lighten-975: rgb(255 255 255 / 0%);
            --cds-color-lighten-950: rgb(255 255 255 / 3%);
            --cds-color-lighten-900: rgb(255 255 255 / 5%);
            --cds-color-lighten-800: rgb(255 255 255 / 10%);
            --cds-color-lighten-700: rgb(255 255 255 / 20%);
            --cds-color-lighten-600: rgb(255 255 255 / 30%);
            --cds-color-lighten-500: rgb(255 255 255 / 40%);
            --cds-color-lighten-400: rgb(255 255 255 / 50%);
            --cds-color-lighten-300: rgb(255 255 255 / 60%);
            --cds-color-lighten-200: rgb(255 255 255 / 70%);
            --cds-color-lighten-100: rgb(255 255 255 / 80%);
            --cds-color-lighten-50: rgb(255 255 255 / 90%);
            --cds-color-lighten-25: rgb(255 255 255 / 95%);
            --cds-color-lighten-0: rgb(255 255 255 / 98%);
            --cds-color-pink-975: #fff2fd;
            --cds-color-pink-950: #ffe0f9;
            --cds-color-pink-900: #ffccf5;
            --cds-color-pink-800: #ffa6ee;
            --cds-color-pink-700: #ff82e7;
            --cds-color-pink-600: #f06bd5;
            --cds-color-pink-500: #e339c4;
            --cds-color-pink-400: #c429a8;
            --cds-color-pink-300: #9c1a84;
            --cds-color-pink-200: #7d166a;
            --cds-color-pink-100: #661057;
            --cds-color-pink-50: #45093a;
            --cds-color-pink-25: #1e051a;
            --cds-color-red-975: #fdf5f5;
            --cds-color-red-950: #fce5e8;
            --cds-color-red-900: #fad2d8;
            --cds-color-red-800: #ffb0bc;
            --cds-color-red-700: #ff8f9f;
            --cds-color-red-600: #fc657d;
            --cds-color-red-500: #f0465f;
            --cds-color-red-400: #ed2644;
            --cds-color-red-300: #d30a28;
            --cds-color-red-200: #c4001d;
            --cds-color-red-100: #9e0018;
            --cds-color-red-50: #66000f;
            --cds-color-red-25: #2e0007;
            --cds-color-green-975: #ebfdf8;
            --cds-color-green-950: #cffaed;
            --cds-color-green-900: #b8f2e1;
            --cds-color-green-800: #94e5cd;
            --cds-color-green-700: #66dab7;
            --cds-color-green-600: #3bc29a;
            --cds-color-green-500: #22a880;
            --cds-color-green-400: #118f69;
            --cds-color-green-300: #087051;
            --cds-color-green-200: #095941;
            --cds-color-green-100: #074735;
            --cds-color-green-50: #062e21;
            --cds-color-green-25: #02150f;
            --cds-color-yellow-975: #fff4e8;
            --cds-color-yellow-950: #ffeac9;
            --cds-color-yellow-900: #ffdc96;
            --cds-color-yellow-800: #ffc936;
            --cds-color-yellow-700: #faaf00;
            --cds-color-yellow-600: #f28100;
            --cds-color-yellow-500: #de6000;
            --cds-color-yellow-400: #c74504;
            --cds-color-yellow-300: #a32e00;
            --cds-color-yellow-200: #852600;
            --cds-color-yellow-100: #6b1e00;
            --cds-color-yellow-50: #471400;
            --cds-color-yellow-25: #200900;
            --cds-color-purple-975: #f9f5ff;
            --cds-color-purple-950: #f1e8ff;
            --cds-color-purple-900: #e7d9ff;
            --cds-color-purple-800: #d3bbfa;
            --cds-color-purple-700: #c29ffc;
            --cds-color-purple-600: #a678f5;
            --cds-color-purple-500: #9258f5;
            --cds-color-purple-400: #8040ed;
            --cds-color-purple-300: #6923de;
            --cds-color-purple-200: #5b15cf;
            --cds-color-purple-100: #4a0fab;
            --cds-color-purple-50: #340385;
            --cds-color-purple-25: #150333;
            --cds-color-blue-975: #f0f6ff;
            --cds-color-blue-950: #e3eeff;
            --cds-color-blue-900: #cfe2ff;
            --cds-color-blue-800: #adcfff;
            --cds-color-blue-700: #87b8ff;
            --cds-color-blue-600: #5b9dfc;
            --cds-color-blue-500: #3587fc;
            --cds-color-blue-400: #1e72eb;
            --cds-color-blue-300: #0056d2;
            --cds-color-blue-200: #0048b0;
            --cds-color-blue-100: #003b8f;
            --cds-color-blue-50: #002761;
            --cds-color-blue-25: #00112a;
            --cds-color-grey-975: #f2f5fa;
            --cds-color-grey-950: #e8eef7;
            --cds-color-grey-900: #dae1ed;
            --cds-color-grey-800: #c1cad9;
            --cds-color-grey-700: #a7b4c9;
            --cds-color-grey-600: #8495b0;
            --cds-color-grey-500: #6d7c99;
            --cds-color-grey-400: #5b6780;
            --cds-color-grey-300: #404b61;
            --cds-color-grey-200: #363f52;
            --cds-color-grey-100: #2d3440;
            --cds-color-grey-50: #1e2229;
            --cds-color-grey-25: #0f1114;
            --cds-color-white-0: #000;
            --cds-color-neutral-background-primary: #0f1114;
            --cds-color-neutral-background-primary-weak: #000;
          }
          @media (prefers-color-scheme: dark) {
            :root[data-color-theme='system'] {
              --cds-color-white-0: rgb(0 0 0 / 100%);
              --cds-color-grey-25: rgb(15 17 20 / 100%);
              --cds-color-grey-50: rgb(30 34 41 / 100%);
              --cds-color-grey-100: rgb(45 52 64 / 100%);
              --cds-color-grey-200: rgb(54 63 82 / 100%);
              --cds-color-grey-300: rgb(64 75 97 / 100%);
              --cds-color-grey-400: rgb(91 103 128 / 100%);
              --cds-color-grey-500: rgb(109 124 153 / 100%);
              --cds-color-grey-600: rgb(132 149 176 / 100%);
              --cds-color-grey-700: rgb(167 180 201 / 100%);
              --cds-color-grey-800: rgb(193 202 217 / 100%);
              --cds-color-grey-900: rgb(218 225 237 / 100%);
              --cds-color-grey-950: rgb(232 238 247 / 100%);
              --cds-color-grey-975: rgb(242 245 250 / 100%);
              --cds-color-blue-25: rgb(0 17 42 / 100%);
              --cds-color-blue-50: rgb(0 39 97 / 100%);
              --cds-color-blue-100: rgb(0 59 143 / 100%);
              --cds-color-blue-200: rgb(0 72 176 / 100%);
              --cds-color-blue-300: rgb(0 86 210 / 100%);
              --cds-color-blue-400: rgb(30 114 235 / 100%);
              --cds-color-blue-500: rgb(53 135 252 / 100%);
              --cds-color-blue-600: rgb(91 157 252 / 100%);
              --cds-color-blue-700: rgb(135 184 255 / 100%);
              --cds-color-blue-800: rgb(173 207 255 / 100%);
              --cds-color-blue-900: rgb(207 226 255 / 100%);
              --cds-color-blue-950: rgb(227 238 255 / 100%);
              --cds-color-blue-975: rgb(240 246 255 / 100%);
              --cds-color-purple-25: rgb(21 3 51 / 100%);
              --cds-color-purple-50: rgb(52 3 133 / 100%);
              --cds-color-purple-100: rgb(74 15 171 / 100%);
              --cds-color-purple-200: rgb(91 21 207 / 100%);
              --cds-color-purple-300: rgb(105 35 222 / 100%);
              --cds-color-purple-400: rgb(128 64 237 / 100%);
              --cds-color-purple-500: rgb(146 88 245 / 100%);
              --cds-color-purple-600: rgb(166 120 245 / 100%);
              --cds-color-purple-700: rgb(194 159 252 / 100%);
              --cds-color-purple-800: rgb(211 187 250 / 100%);
              --cds-color-purple-900: rgb(231 217 255 / 100%);
              --cds-color-purple-950: rgb(241 232 255 / 100%);
              --cds-color-purple-975: rgb(249 245 255 / 100%);
              --cds-color-yellow-25: rgb(32 9 0 / 100%);
              --cds-color-yellow-50: rgb(71 20 0 / 100%);
              --cds-color-yellow-100: rgb(107 30 0 / 100%);
              --cds-color-yellow-200: rgb(133 38 0 / 100%);
              --cds-color-yellow-300: rgb(163 46 0 / 100%);
              --cds-color-yellow-400: rgb(199 69 4 / 100%);
              --cds-color-yellow-500: rgb(222 96 0 / 100%);
              --cds-color-yellow-600: rgb(242 129 0 / 100%);
              --cds-color-yellow-700: rgb(250 175 0 / 100%);
              --cds-color-yellow-800: rgb(255 201 54 / 100%);
              --cds-color-yellow-900: rgb(255 220 150 / 100%);
              --cds-color-yellow-950: rgb(255 234 201 / 100%);
              --cds-color-yellow-975: rgb(255 244 232 / 100%);
              --cds-color-green-25: rgb(2 21 15 / 100%);
              --cds-color-green-50: rgb(6 46 33 / 100%);
              --cds-color-green-100: rgb(7 71 53 / 100%);
              --cds-color-green-200: rgb(9 89 65 / 100%);
              --cds-color-green-300: rgb(8 112 81 / 100%);
              --cds-color-green-400: rgb(17 143 105 / 100%);
              --cds-color-green-500: rgb(34 168 128 / 100%);
              --cds-color-green-600: rgb(59 194 154 / 100%);
              --cds-color-green-700: rgb(102 218 183 / 100%);
              --cds-color-green-800: rgb(148 229 205 / 100%);
              --cds-color-green-900: rgb(184 242 225 / 100%);
              --cds-color-green-950: rgb(207 250 237 / 100%);
              --cds-color-green-975: rgb(235 253 248 / 100%);
              --cds-color-red-25: rgb(46 0 7 / 100%);
              --cds-color-red-50: rgb(102 0 15 / 100%);
              --cds-color-red-100: rgb(158 0 24 / 100%);
              --cds-color-red-200: rgb(196 0 29 / 100%);
              --cds-color-red-300: rgb(211 10 40 / 100%);
              --cds-color-red-400: rgb(237 38 68 / 100%);
              --cds-color-red-500: rgb(240 70 95 / 100%);
              --cds-color-red-600: rgb(252 101 125 / 100%);
              --cds-color-red-700: rgb(255 143 159 / 100%);
              --cds-color-red-800: rgb(255 176 188 / 100%);
              --cds-color-red-900: rgb(250 210 216 / 100%);
              --cds-color-red-950: rgb(252 229 232 / 100%);
              --cds-color-red-975: rgb(253 245 245 / 100%);
              --cds-color-pink-25: rgb(30 5 26 / 100%);
              --cds-color-pink-50: rgb(69 9 58 / 100%);
              --cds-color-pink-100: rgb(102 16 87 / 100%);
              --cds-color-pink-200: rgb(125 22 106 / 100%);
              --cds-color-pink-300: rgb(156 26 132 / 100%);
              --cds-color-pink-400: rgb(196 41 168 / 100%);
              --cds-color-pink-500: rgb(227 57 196 / 100%);
              --cds-color-pink-600: rgb(240 107 213 / 100%);
              --cds-color-pink-700: rgb(255 130 231 / 100%);
              --cds-color-pink-800: rgb(255 166 238 / 100%);
              --cds-color-pink-900: rgb(255 204 245 / 100%);
              --cds-color-pink-950: rgb(255 224 249 / 100%);
              --cds-color-pink-975: rgb(255 242 253 / 100%);
              --cds-color-lighten-0: rgb(255 255 255 / 98%);
              --cds-color-lighten-25: rgb(255 255 255 / 95%);
              --cds-color-lighten-50: rgb(255 255 255 / 90%);
              --cds-color-lighten-100: rgb(255 255 255 / 80%);
              --cds-color-lighten-200: rgb(255 255 255 / 70%);
              --cds-color-lighten-300: rgb(255 255 255 / 60%);
              --cds-color-lighten-400: rgb(255 255 255 / 50%);
              --cds-color-lighten-500: rgb(255 255 255 / 40%);
              --cds-color-lighten-600: rgb(255 255 255 / 30%);
              --cds-color-lighten-700: rgb(255 255 255 / 20%);
              --cds-color-lighten-800: rgb(255 255 255 / 10%);
              --cds-color-lighten-900: rgb(255 255 255 / 5%);
              --cds-color-lighten-950: rgb(255 255 255 / 3%);
              --cds-color-lighten-975: rgb(255 255 255 / 0%);
              --cds-color-darken-0: rgb(15 17 20 / 98%);
              --cds-color-darken-25: rgb(15 17 20 / 95%);
              --cds-color-darken-50: rgb(15 17 20 / 90%);
              --cds-color-darken-100: rgb(15 17 20 / 80%);
              --cds-color-darken-200: rgb(15 17 20 / 70%);
              --cds-color-darken-300: rgb(15 17 20 / 60%);
              --cds-color-darken-400: rgb(15 17 20 / 50%);
              --cds-color-darken-500: rgb(15 17 20 / 40%);
              --cds-color-darken-600: rgb(15 17 20 / 30%);
              --cds-color-darken-700: rgb(15 17 20 / 20%);
              --cds-color-darken-800: rgb(15 17 20 / 10%);
              --cds-color-darken-900: rgb(15 17 20 / 5%);
              --cds-color-darken-950: rgb(15 17 20 / 3%);
              --cds-color-darken-975: rgb(15 17 20 / 0%);
              --cds-color-neutral-background-primary: #0f1114;
              --cds-color-neutral-background-primary-weak: #000;
            }
          }
          /* stylelint-enable function-disallowed-list */
        `}
      />
      {children}
    </ColorThemeContext.Provider>
  );
};
